import { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { CommentsFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { ChatAnswerFormContainerProps } from "models/propTypes/commentViewPropTypes";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
import { StringSchema } from "yup";
import * as Yup from "yup";
//#endregion
//#region components
import { ChatAnswerForm } from "components/common/comments";
import MessageWithTooltip from "components/common/messages/messageWithTooltip";
import { Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const ChatAnswerFormContainer: ForwardRefRenderFunction<FormHandles, ChatAnswerFormContainerProps> = (props, ref) => {
  const {
    initialValues: initialValuesFromProps,
    handleSubmit,
    commentOrFileRequired,
    hasMultipleRecipients,
    isConfidential,
    isOwnedByAgency,
  } = props;

  const initialValues: CommentsFormTypes.CommentWithFile = {
    explanation: "",
    filesToUpload: [],
    postWithoutPicture: false,
    markAsResolved: false,
    ...initialValuesFromProps,
  };

  const validationSchema = Yup.object<CommentsFormTypes.CommentWithFile>().shape(
    {
      explanation: Yup.string().when(
        [
          FormFieldConstants.Comments.CommentWithFile.MarkAsResolved,
          FormFieldConstants.Comments.CommentWithFile.FilesToUpload,
        ],
        ([markAsResolved, filesToUpload], schema: StringSchema) =>
          (((!commentOrFileRequired && !markAsResolved) || (commentOrFileRequired && !filesToUpload?.length)) &&
            schema.required(TranslationKeys.Validations.Required)) ||
          schema
      ),
      markAsResolved: Yup.boolean(),
      postWithoutPicture: Yup.boolean(),
      filesToUpload: Yup.array()
        .of(
          Yup.object<UploadedFile>({
            contentType: Yup.string(),
            fileName: Yup.string(),
            fileSize: Yup.number(),
            data: Yup.string(),
          })
        )
        .when(
          [
            FormFieldConstants.Comments.CommentWithFile.PostWithoutPicture,
            FormFieldConstants.Comments.CommentWithFile.Explanation,
          ],
          ([postWithoutPicture, explanation]: [boolean, string], schema: ArraySchema<UploadedFile>) =>
            (((!commentOrFileRequired && postWithoutPicture === false) || (commentOrFileRequired && !explanation)) &&
              schema.required(TranslationKeys.Validations.Required)) ||
            schema
        ),
    },
    [
      [
        FormFieldConstants.Comments.CommentWithFile.Explanation,
        FormFieldConstants.Comments.CommentWithFile.FilesToUpload,
      ],
    ]
  );

  const handleSubmitDefault = async (values: CommentsFormTypes.CommentWithFile) => values;

  const banner = isConfidential
    ? hasMultipleRecipients
      ? {
          message: TranslationKeys.Common.ConfidentialMultipleRecipientsMessage,
          toolTip: TranslationKeys.Common.ConfidentialMultipleRecipientsToolTip,
        }
      : {
          message: TranslationKeys.Common.ConfidentialCaseMessage,
          toolTip: TranslationKeys.Common.ConfidentialCaseToolTip,
        }
    : hasMultipleRecipients
      ? {
          message: TranslationKeys.Common.MultipleRecipientsMessage,
          // A byrå-owned case goes to the byrå, not to Wint — the ordinary tooltip says the opposite.
          toolTip: isOwnedByAgency
            ? TranslationKeys.Common.AgencyMultipleRecipientsToolTip
            : TranslationKeys.Common.MultipleRecipientsToolTip,
        }
      : undefined;

  return (
    <Grid container direction="column" rowGap={1}>
      {banner && (
        <MessageWithTooltip toolTip={LocalizationHelperInstance.getFormattedMessage(banner.toolTip)}>
          {LocalizationHelperInstance.getFormattedMessage(banner.message)}
        </MessageWithTooltip>
      )}

      <ChatAnswerForm
        ref={ref}
        {...props}
        validationSchema={validationSchema}
        defaultValues={initialValues}
        handleSubmit={handleSubmit ?? handleSubmitDefault}
      />
    </Grid>
  );
};

export default forwardRef(ChatAnswerFormContainer);
